import { Component, lazy, Suspense, useEffect } from 'react';
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import PropTypes from 'prop-types'
import loadWithReducers from '../../store/loadWithReducers'

const CHUNK_RELOAD_STORAGE_PREFIX = 'assethub:chunk-reload:'

export const isChunkLoadError = (error = {}) =>
  error.name === 'ChunkLoadError' ||
  error.code === 'CSS_CHUNK_LOAD_FAILED' ||
  /Loading (CSS )?chunk .+ failed/i.test(error.message || '')

const getReloadStorageKey = (recoveryKey = '') =>
  `${CHUNK_RELOAD_STORAGE_PREFIX}${window.location.pathname}:${recoveryKey}`

const clearReloadAttempt = (recoveryKey) => {
  try {
    window.sessionStorage.removeItem(getReloadStorageKey(recoveryKey))
  } catch {
    // Storage can be unavailable when cookies/site data are disabled.
  }
}

const markReloadAttempt = (recoveryKey) => {
  try {
    const storageKey = getReloadStorageKey(recoveryKey)
    if (window.sessionStorage.getItem(storageKey)) {
      return false
    }

    window.sessionStorage.setItem(storageKey, 'true')
    return true
  } catch {
    // Do not auto-reload without a working guard against reload loops.
    return false
  }
}

export const RouteLoadingFallback = () => (
  <div
    role="status"
    aria-label="Loading page"
    style={{
      minHeight: 240,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress size={32} />
  </div>
)

export const RouteLoadSuccess = ({ recoveryKey }) => {
  useEffect(() => {
    clearReloadAttempt(recoveryKey)
  }, [recoveryKey])

  return null
}

RouteLoadSuccess.defaultProps = {
  recoveryKey: '',
}

RouteLoadSuccess.propTypes = {
  recoveryKey: PropTypes.string,
}

const RouteErrorFallback = ({ error, reloadPage }) => {
  const chunkLoadFailed = isChunkLoadError(error)
  const isOffline = navigator.onLine === false
  let message = 'The page could not be displayed.'

  if (isOffline) {
    message = 'You appear to be offline. Check your connection and try again.'
  } else if (chunkLoadFailed) {
    message = 'The page did not load correctly. Reload to try again.'
  }

  return (
    <div
      role="alert"
      style={{
        minHeight: 240,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      <p>{message}</p>
      <Button variant="contained" onClick={reloadPage}>
        Reload page
      </Button>
    </div>
  )
}

RouteErrorFallback.propTypes = {
  error: PropTypes.instanceOf(Error).isRequired,
  reloadPage: PropTypes.func.isRequired,
}

export class RouteErrorBoundary extends Component {
  state = { error: null }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error) {
    const { recoveryKey, reloadPage } = this.props

    if (
      isChunkLoadError(error) &&
      navigator.onLine !== false &&
      markReloadAttempt(recoveryKey)
    ) {
      reloadPage()
    }
  }

  render() {
    const { error } = this.state
    const { children, reloadPage } = this.props

    if (error) {
      return <RouteErrorFallback error={error} reloadPage={reloadPage} />
    }

    return children
  }
}

RouteErrorBoundary.defaultProps = {
  recoveryKey: '',
  reloadPage: () => window.location.reload(),
}

RouteErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  recoveryKey: PropTypes.string,
  reloadPage: PropTypes.func,
}

export const lazyRoute = (loadRoute, reducerLoaders = {}) => {
  const LazyRouteComponent = lazy(() =>
    loadWithReducers(loadRoute, reducerLoaders),
  )

  return function LazyRoute(props) {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <LazyRouteComponent {...props} />
          <RouteLoadSuccess />
        </Suspense>
      </RouteErrorBoundary>
    )
  }
}
